/**
 * 预测市场 API 服务
 * 代理 xguess-server-node 服务端接口
 */
import qs from 'qs';
import { captureRequest } from '@/lib/analytics/posthog-events';
import { apiClient } from './api';
import { formatGroupItemTitle } from '@/app/predict/utils';
import { CLOB_API_URL } from '@/lib/polymarket-clob';
import { useUserStore } from '@/app/stores/useUserStore';

/** 原生 fetch 埋点（走 `apiClient` 的请求已在 `lib/api` 统一埋点） */
async function fetchWithCapture(action: string, input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const safeAction = action.slice(0, 300);
  try {
    const response = await fetch(input, init);
    if (!response.ok) {
      captureRequest({
        action: safeAction,
        status: 'fail',
        code: response.status,
        msg: response.statusText?.slice(0, 200),
      });
    } else {
      captureRequest({
        action: safeAction,
        status: 'success',
        code: response.status,
      });
    }
    return response;
  } catch (e) {
    captureRequest({
      action: safeAction,
      status: 'fail',
      msg: e instanceof Error ? e.message.slice(0, 200) : 'network_error',
    });
    throw e;
  }
}

/**
 * 将系统语言映射到 Polymarket locale
 * Polymarket 支持: en, zh, ja, ko, de, es, fr, ru
 * 系统支持: en-US, zh-CN, zh-TW, ja-JP, ko-KR
 */
export function getPolymarketLocale(systemLocale: string): string | undefined {
  const localeMap: Record<string, string> = {
    'zh-CN': 'zh',
    'zh-TW': 'zh', // Polymarket 不支持繁体，降级到简体
    'ja-JP': 'ja',
    'ko-KR': 'ko',
    'en-US': 'en',
  };
  return localeMap[systemLocale];
}

// ============ 类型定义 ============

/** 分类类型 */
export interface PredictCategory {
  id: string;
  label: string;
  slug: string;
  parentCategory?: string;
}

/** 标签类型 */
export interface PredictTag {
  id: string;
  label: string;
  slug: string;
  forceShow?: boolean;
  forceHide?: boolean;
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** 市场类型 */
export interface PredictMarket {
  id: string;
  question: string;
  slug: string;
  image?: string;
  icon?: string;
  outcomes: string;
  outcomePrices: string;
  volume: string;
  active: boolean;
  closed: boolean;
  groupItemTitle?: string;
  groupItemThreshold?: string;
  description?: string;
  resolutionSource?: string;
  bestBid?: number;
  bestAsk?: number;
  lastTradePrice?: number;
  createdAt?: string;
  conditionId?: string;
  clobTokenIds?: string;
  oneWeekPriceChange?: number;
  oneDayPriceChange?: number;
  oneMonthPriceChange?: number;
  // 订单价格最小 tick size（来自 Polymarket API）
  orderPriceMinTickSize?: number;
  // 体育市场专用字段
  sportsMarketType?:
    | 'moneyline'
    | 'spreads'
    | 'totals'
    | 'points'
    | 'rebounds'
    | 'assists'
    | 'first_half_moneyline'
    | 'first_half_spreads'
    | 'first_half_totals';
  line?: number;
  // Polymarket CLOB 手续费字段(V2 起由后端 Permit2 扣 1% pUSD,前端不再透传 feeRateBps)
  // 仅作为展示参考保留;V2 SDK 已不接受订单上的 feeRateBps
  makerBaseFee?: number;
  takerBaseFee?: number;
  feesEnabled?: boolean;
  feeType?: string;
}

/** 系列类型 */
export interface PredictSeries {
  id: string;
  ticker: string;
  slug: string;
  title: string;
  commentCount?: number;
}

/** 事件类型 */
export interface PredictEvent {
  id: string;
  slug: string;
  title: string;
  description: string;
  image?: string;
  icon?: string;
  category: string;
  volume: number;
  volume24hr: number;
  active: boolean;
  closed: boolean;
  markets: PredictMarket[];
  series?: PredictSeries[];
  commentCount?: number;
  startDate?: string;
  startTime?: string; // 实际比赛开始时间 (体育赛事专用)
  endDate?: string;
  tags?: PredictTag[];
  featured?: boolean; // 是否为置顶事件
  // Live 相关字段
  live?: boolean; // 是否正在进行
  ended?: boolean; // 是否已结束
  score?: string; // 当前比分 (如 "114-120")
  elapsed?: string; // 已用时间
  period?: string; // 当前阶段 (如 "Q1", "Q2", "VFT"=结束)
}

/** 持仓者类型 */
export interface PredictHolder {
  proxyWallet: string;
  name: string;
  pseudonym: string;
  profileImage: string;
  amount: number;
  outcomeIndex: number;
  displayUsernamePublic?: boolean;
}

/** 用户资料类型 */
export interface PredictUserProfile {
  name?: string;
  pseudonym?: string;
  displayUsernamePublic?: boolean;
  proxyWallet?: string;
  baseAddress?: string;
  profileImage?: string;
}

/** 价格历史数据点 */
export interface PredictPricePoint {
  t: number;
  p: number;
}

/** 价格历史响应 */
export interface PredictPriceHistoryResponse {
  history: PredictPricePoint[];
}

/** 交易类型 */
export interface PredictTrade {
  proxyWallet: string;
  side: 'BUY' | 'SELL';
  asset: string;
  conditionId: string;
  size: number;
  price: number;
  timestamp: number;
  title: string;
  slug: string;
  icon: string;
  eventSlug: string;
  outcome: string;
  outcomeIndex: number;
  name: string;
  pseudonym: string;
  profileImage: string;
  transactionHash: string;
}

/** 用户持仓类型 */
export interface UserPosition {
  proxyWallet: string;
  asset: string;
  conditionId: string;
  size: number;
  avgPrice: number;
  initialValue: number;
  currentValue: number;
  cashPnl: number;
  percentPnl: number;
  totalBought: number;
  realizedPnl: number;
  percentRealizedPnl: number;
  curPrice: number;
  redeemable: boolean;
  mergeable: boolean;
  title: string;
  slug: string;
  icon: string;
  eventSlug: string;
  eventId?: string;
  outcome: string;
  outcomeIndex: number;
  oppositeOutcome: string;
  oppositeAsset: string;
  endDate: string;
  negativeRisk: boolean;
}

/** 用户持仓价值类型 */
export interface UserValue {
  user: string;
  value: number;
}

/** 用户活动记录类型（交易历史） */
export interface UserActivity {
  id?: string;
  type: string; // "TRADE", "REDEEM" 等
  size: number;
  usdcSize: number | string;
  price: number;
  side: string; // "BUY", "SELL"
  title: string;
  outcome: string;
  slug: string; // market slug
  eventSlug?: string; // event slug (用于跳转详情页)
  icon?: string;
  timestamp: string;
  transactionHash?: string;
  conditionId?: string;
  asset?: string;
  amount?: number;
}

/** Polymarket 运动/联赛类型 */
export interface PolymarketSport {
  id: number;
  sport: string; // 联赛简称 (nfl, nba, epl...)
  image: string; // Logo URL
  resolution: string; // 官网
  ordering: string; // home/away
  tags: string; // 逗号分隔的 tag ids
  series: string; // series_id，用于查询比赛
  createdAt: string;
}

/** Polymarket 队伍类型 */
export interface PolymarketTeam {
  id: number;
  name: string | null;
  league: string | null;
  record: string | null;
  logo: string | null;
  abbreviation: string | null;
  alias: string | null;
  // 队伍主题色（API 实际有返回，用于 UI 染色）
  color?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Sports 事件查询参数 */
export interface GetSportsEventsParams {
  series_id: string;
  active?: boolean;
  closed?: boolean;
  order?: string;
  ascending?: boolean;
  limit?: number;
  locale?: string; // 系统语言，用于多语言支持
}

// ============ Polymarket Bridge Supported Assets ============

/** Bridge 支持的代币信息 */
export interface SupportedAssetToken {
  name: string;
  symbol: string;
  address: string;
  decimals: number;
}

/** Bridge 支持的资产项（链 + 代币 + 最小金额） */
export interface SupportedAsset {
  chainId: string;
  chainName: string;
  token: SupportedAssetToken;
  minCheckoutUsd: number;
}

/** supported-assets 接口返回值 */
export interface SupportedAssetsResponse {
  supportedAssets: SupportedAsset[];
  note?: string;
}

const POLYMARKET_BRIDGE_BASE = 'https://bridge.polymarket.com';
const POLYMARKET_BRIDGE_ASSETS_URL = `${POLYMARKET_BRIDGE_BASE}/supported-assets`;
const POLYMARKET_BRIDGE_DEPOSIT_URL = `${POLYMARKET_BRIDGE_BASE}/deposit`;
const POLYMARKET_BRIDGE_QUOTE_URL = `${POLYMARKET_BRIDGE_BASE}/quote`;
const POLYMARKET_BRIDGE_WITHDRAW_URL = `${POLYMARKET_BRIDGE_BASE}/withdraw`;

/** Polygon 上 Polymarket Bridge 接受的源 token 地址 */
/** V1 时代:USDC.e */
export const POLYGON_USDC_E_BRIDGE = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';
/** V2 时代:pUSD(Polymarket 切换日后 Bridge 改为接受 pUSD,前端跟随 store.version) */
export const POLYGON_PUSD_BRIDGE = '0xC011a7E12a19f7B1f670d46F03B03f3342E82DFB';
export const POLYGON_CHAIN_ID_STR = '137';

/** POST /quote 请求体 */
export interface BridgeQuoteRequest {
  fromAmountBaseUnit: string;
  fromChainId: string;
  fromTokenAddress: string;
  recipientAddress: string;
  toChainId: string;
  toTokenAddress: string;
}

/** POST /quote 响应（字段随 Bridge 版本可能扩展） */
export interface BridgeQuoteResponse {
  quoteId: string;
  estCheckoutTimeMs?: number;
  estInputUsd?: number;
  estOutputUsd?: number;
  estToTokenBaseUnit?: string;
  estFeeBreakdown?: {
    totalImpactUsd?: number;
    minReceived?: number;
    gasUsd?: number;
    fillCostUsd?: number;
    [key: string]: unknown;
  };
}

/** POST /withdraw 请求体，见 https://docs.polymarket.com/api-reference/bridge/create-withdrawal-addresses */
export interface BridgeWithdrawRequest {
  address: string;
  toChainId: string;
  toTokenAddress: string;
  recipientAddr: string;
}

export interface BridgeWithdrawAddressBundle {
  evm?: string;
  svm?: string;
  btc?: string;
  tron?: string;
}

export interface BridgeWithdrawResponse {
  address: BridgeWithdrawAddressBundle;
  note?: string;
}

export interface BridgeStatusTransaction {
  fromChainId?: string;
  fromTokenAddress?: string;
  fromAmountBaseUnit?: string;
  toChainId?: string;
  toTokenAddress?: string;
  status?: string;
  txHash?: string;
  createdTimeMs?: number;
}

export interface BridgeStatusResponse {
  transactions: BridgeStatusTransaction[];
}

/** 获取 Polymarket Bridge 支持的链与代币列表 */
export async function getSupportedAssets(): Promise<SupportedAssetsResponse> {
  const res = await fetchWithCapture('predict:bridge/supported-assets', POLYMARKET_BRIDGE_ASSETS_URL);
  if (!res.ok) {
    throw new Error(`Supported assets request failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/** 请求 Polymarket Bridge 充值（POST，入参 address） */
export async function postBridgeDeposit(address: string): Promise<unknown> {
  const res = await fetchWithCapture('predict:bridge/deposit', POLYMARKET_BRIDGE_DEPOSIT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address }),
  });
  if (!res.ok) {
    throw new Error(`Bridge deposit request failed: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/** Polymarket Bridge：预估跨链报价（费用、预计到账等） */
export async function postBridgeQuote(body: BridgeQuoteRequest): Promise<BridgeQuoteResponse> {
  const res = await fetchWithCapture('predict:bridge/quote', POLYMARKET_BRIDGE_QUOTE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `Bridge quote failed: ${res.status}`);
  }
  return res.json();
}

/** Polymarket Bridge：创建提现桥接收款地址（用户需向返回的 Polygon EVM 地址转 USDC.e） */
export async function postBridgeWithdraw(body: BridgeWithdrawRequest): Promise<BridgeWithdrawResponse> {
  const res = await fetchWithCapture('predict:bridge/withdraw', POLYMARKET_BRIDGE_WITHDRAW_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `Bridge withdraw failed: ${res.status}`);
  }
  return res.json();
}

/** Polymarket Bridge：查询 /deposit 或 /withdraw 返回地址上的交易状态 */
export async function getBridgeTransactionStatus(address: string): Promise<BridgeStatusResponse> {
  const encoded = encodeURIComponent(address);
  const res = await fetchWithCapture('predict:bridge/status', `${POLYMARKET_BRIDGE_BASE}/status/${encoded}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `Bridge status failed: ${res.status}`);
  }
  return res.json();
}

// ============ API 配置 ============

/** 预测服务端地址 (走 testDomain/prodDomain) */
const PREDICT_API_BASE = '/app/prediction';

/**
 * 接口迁移配置
 * key: 原接口路径, value: { base: 新服务端地址, path: 新路径 }
 */
const API_MIGRATION_CONFIG: Record<string, { base: string; path: string }> = {
  // 已迁移的接口 - Live API
  '/listing/list': { base: PREDICT_API_BASE, path: '/listing/list' },
  '/listing': { base: PREDICT_API_BASE, path: '/listing' },
  // 已迁移的接口 - Gamma API
  '/gamma/categories': { base: PREDICT_API_BASE, path: '/categories' },
  '/gamma/events': { base: PREDICT_API_BASE, path: '/events' },
  '/gamma/search': { base: PREDICT_API_BASE, path: '/search' },
  '/gamma/sports': { base: PREDICT_API_BASE, path: '/sports' },
  '/gamma/teams': { base: PREDICT_API_BASE, path: '/teams' },
  '/gamma/markets': { base: PREDICT_API_BASE, path: '/markets' },
  // 已迁移的接口 - Data API (直接调用官方，无跨域限制)
  '/data/holders': { base: 'https://data-api.polymarket.com', path: '/holders' },
  '/data/trades': { base: 'https://data-api.polymarket.com', path: '/trades' },
  '/data/value': { base: 'https://data-api.polymarket.com', path: '/value' },
  '/data/positions': { base: 'https://data-api.polymarket.com', path: '/positions' },
  '/data/activity': { base: 'https://data-api.polymarket.com', path: '/activity' },
  // 已迁移的接口 - CLOB API (直接调用官方,无跨域限制)
  // base 跟 polymarket-clob.ts 的 CLOB_API_URL 走:V1/V2 host 都支持这三个 read-only 端点且格式一致
  '/clob/prices-history': { base: CLOB_API_URL, path: '/prices-history' },
  '/clob/tick-size': { base: CLOB_API_URL, path: '/tick-size' },
  '/clob/neg-risk': { base: CLOB_API_URL, path: '/neg-risk' },
};

/** 获取接口的实际 URL */
function getEndpointUrl(endpoint: string): string {
  // 检查是否有迁移配置
  for (const [pattern, config] of Object.entries(API_MIGRATION_CONFIG)) {
    if (endpoint.startsWith(pattern)) {
      // 替换路径前缀
      const suffix = endpoint.slice(pattern.length);
      return `${config.base}${config.path}${suffix}`;
    }
  }
  return `${PREDICT_API_BASE}${endpoint}`;
}

// ============ 请求封装 ============

/** 判断是否走服务端接口（需要 token） */
function isServerEndpoint(endpoint: string): boolean {
  return endpoint.startsWith('/gamma/') || endpoint.startsWith('/listing');
}

/**
 * 通用请求方法
 * - 服务端接口(/gamma/*)：使用 apiClient（自动带 token）
 * - 官方 API（Data/CLOB）：使用原生 fetch
 */
async function request<T>(endpoint: string, options: RequestInit & { needResponse?: boolean } = {}): Promise<T> {
  const url = getEndpointUrl(endpoint);

  // 走服务端的接口使用 apiClient
  if (isServerEndpoint(endpoint)) {
    const response = options?.method === 'POST' ? await apiClient.post<T>(url, options.body) : await apiClient.get<T>(url);
    // 兼容两种返回格式：{ data: T } 或直接返回 T
    if (!options?.needResponse && response && typeof response === 'object' && 'data' in response && !Array.isArray(response)) {
      return (response as { data: T }).data;
    }
    return response as unknown as T;
  }

  // 直接调用官方 API 使用原生 fetch
  const response = await fetchWithCapture(`predict:fetch${endpoint.slice(0, 280)}`, url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Predict API Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * 构建查询字符串
 */
function buildQueryString(params: Record<string, unknown> | object): string {
  const searchParams = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

// ============ API 方法 ============

/** 事件列表查询参数 */
export interface GetEventsParams {
  limit?: number;
  offset?: number;
  active?: boolean;
  closed?: boolean;
  archived?: boolean;
  tag_slug?: string; // 标签过滤
  order?: string;
  ascending?: boolean;
  locale?: string; // 系统语言，用于多语言支持
}

/** 价格历史查询参数 */
export interface GetPriceHistoryParams {
  market: string;
  interval?: string;
  fidelity?: number;
  startTs?: number;
  endTs?: number;
}

/** 持仓者查询参数 */
export interface GetHoldersParams {
  market: string; // conditionId
  limit?: number;
}

/** 持仓者响应类型 */
export interface HoldersResponse {
  outcome: string;
  outcomeIndex: number;
  holders: PredictHolder[];
}

/** 交易记录查询参数 */
export interface GetTradesParams {
  market: string; // conditionId
  limit?: number;
  filterType?: 'CASH' | 'TOKENS'; // 过滤类型：CASH 按金额，TOKENS 按数量
  filterAmount?: number; // 过滤阈值
}

/** 用户持仓查询参数 */
export interface GetUserPositionsParams {
  user: string; // Safe 地址
  sizeThreshold?: number;
  limit?: number;
}

/** 用户活动查询参数 */
export interface GetUserActivityParams {
  user: string; // Safe 地址
  limit?: number;
  offset?: number;
  excludeDepositsWithdrawals?: boolean;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export interface SearchQuery {
  q: string;
  locale?: string;
  events_status?: string;
  type?: string;
  limit_per_type?: number;
  offset?: number;
  page?: number;
  sort?: string;
  presets?: string;
}

function normalizeMarket(market: PredictMarket): PredictMarket {
  return {
    ...market,
    groupItemTitle: formatGroupItemTitle(market.groupItemTitle || ''),
  };
}

function normalizeMarkets(markets: PredictMarket[] = []): PredictMarket[] {
  return markets.map(normalizeMarket);
}

function normalizeEvent(event: PredictEvent): PredictEvent {
  return {
    ...event,
    markets: normalizeMarkets(event.markets),
  };
}

function normalizeEvents(events: PredictEvent[] = []): PredictEvent[] {
  return events.map(normalizeEvent);
}

/**
 * 预测市场 API 服务
 */
export const predictApi = {
  // ============ Live API ============
  /** 获取上架列表 */
  getUpEvents: async () => {
    const events = await request<PredictEvent[]>('/listing/list');
    return normalizeEvents(events);
  },

  /** 上架和下架 */
  listEvent: (data: { slug: string; status: number; title: string; icon: string }) => {
    return request<any>('/listing', {
      method: 'POST',
      body: JSON.stringify(data),
      needResponse: true,
    });
  },

  // ============ Gamma API ============

  /** 获取事件列表 */
  getEvents: async (params: GetEventsParams = {}) => {
    const { locale: systemLocale, ...restParams } = params;
    const polyLocale = systemLocale ? getPolymarketLocale(systemLocale) : undefined;
    const queryParams = polyLocale ? { ...restParams, locale: polyLocale } : restParams;
    const events = await request<PredictEvent[]>(`/gamma/events${buildQueryString(queryParams)}`);
    return normalizeEvents(events);
  },

  /** 搜索事件 */
  /**
   * 搜索事件
   * @param q 搜索关键词
   * @param events_status 活跃事件
   * @param type 类型
   * @param limit_per_type 每类型限制
   * @param sort 排序
   * @param presets 预设类型
   * @returns 搜索结果
   */
  search: async (params: SearchQuery) => {
    const { locale: systemLocale, ...restParams } = params;
    const polyLocale = systemLocale ? getPolymarketLocale(systemLocale) : undefined;
    const queryParams = polyLocale ? { ...restParams, locale: polyLocale } : restParams;
    const result = await request<{ events: PredictEvent[]; pagination: { totalResults: number; hasMore: boolean } }>(
      `/gamma/search?${qs.stringify(queryParams)}`
    );
    return {
      ...result,
      events: normalizeEvents(result.events),
    };
  },

  /** 获取事件详情（通过 slug） */
  getEventBySlug: async (slug: string, systemLocale?: string): Promise<PredictEvent | null> => {
    const polyLocale = systemLocale ? getPolymarketLocale(systemLocale) : undefined;
    const localeParam = polyLocale ? `&locale=${polyLocale}` : '';
    const events = await request<PredictEvent[]>(`/gamma/events?slug=${slug}${localeParam}`);
    return events[0] ? normalizeEvent(events[0]) : null;
  },

  /** 获取分类列表 */
  getCategories: async (): Promise<PredictCategory[]> => {
    // 服务端返回字段与前端类型有差异，需要映射
    interface ServerCategory {
      id: string;
      label: string;
      slug: string;
      parent_id?: string | null;
    }
    const data = await request<ServerCategory[]>('/gamma/categories');
    // 字段映射: parent_id → parentCategory
    return data.map(item => ({
      id: item.id,
      label: item.label,
      slug: item.slug,
      parentCategory: item.parent_id || undefined,
    }));
  },

  // ============ CLOB API ============

  /** 获取价格历史 (通过 clobTokenId) */
  getPriceHistory: (params: GetPriceHistoryParams) => {
    return request<PredictPriceHistoryResponse>(`/clob/prices-history${buildQueryString(params)}`);
  },

  /** 获取市场的 tick size */
  getTickSize: async (tokenId: string): Promise<string> => {
    try {
      const data = await request<{ minimum_tick_size?: number }>(`/clob/tick-size?token_id=${tokenId}`);
      if (data?.minimum_tick_size !== undefined) {
        return String(data.minimum_tick_size);
      }
      return '0.01';
    } catch (error) {
      console.warn('[PredictAPI] Failed to get tick size, using default:', error);
      return '0.01';
    }
  },

  /** 获取市场的 negRisk 状态 */
  getNegRisk: async (tokenId: string): Promise<boolean> => {
    try {
      const data = await request<{ neg_risk?: boolean }>(`/clob/neg-risk?token_id=${tokenId}`);
      return data?.neg_risk === true;
    } catch (error) {
      console.warn('[PredictAPI] Failed to get negRisk, using default:', error);
      return false;
    }
  },

  // ============ Data API ============

  /** 获取持仓者列表 */
  getHolders: (params: GetHoldersParams) => {
    return request<HoldersResponse[]>(`/data/holders${buildQueryString(params)}`);
  },

  /** 获取交易记录 */
  getTrades: (params: GetTradesParams) => {
    return request<PredictTrade[]>(`/data/trades${buildQueryString(params)}`);
  },

  /** 获取用户持仓价值 */
  getUserValue: async (userAddress: string): Promise<number> => {
    try {
      const data = await request<UserValue[]>(`/data/value?user=${userAddress}`);
      return data.length > 0 ? data[0].value : 0;
    } catch (error) {
      console.error('[PredictAPI] 获取用户持仓价值失败:', error);
      return 0;
    }
  },

  /** 获取用户持仓列表 */
  getUserPositions: (params: GetUserPositionsParams) => {
    const queryParams = {
      user: params.user,
      sizeThreshold: params.sizeThreshold ?? 0.01,
      limit: params.limit ?? 500,
    };
    return request<UserPosition[]>(`/data/positions${buildQueryString(queryParams)}`);
  },

  /** 获取用户活动历史 */
  getUserActivity: (params: GetUserActivityParams) => {
    const queryParams = {
      user: params.user,
      limit: params.limit ?? 100,
      offset: params.offset ?? 0,
      excludeDepositsWithdrawals: params.excludeDepositsWithdrawals ?? false,
      sortBy: params.sortBy ?? 'TIMESTAMP',
      sortDirection: params.sortDirection ?? 'DESC',
    };
    return request<UserActivity[]>(`/data/activity${buildQueryString(queryParams)}`);
  },

  // ============ Sports API ============

  /** 获取所有运动/联赛列表 */
  getSports: (): Promise<PolymarketSport[]> => {
    return request<PolymarketSport[]>('/gamma/sports');
  },

  /** 获取队伍列表 */
  getTeams: (params?: { league?: string; limit?: number; offset?: number }): Promise<PolymarketTeam[]> => {
    const queryParams = {
      league: params?.league,
      offset: params?.offset,
      limit: params?.limit ?? 500,
    };
    return request<PolymarketTeam[]>(`/gamma/teams${buildQueryString(queryParams)}`);
  },

  /** 获取指定联赛的比赛事件（未结算的） */
  getSportsEvents: async (params: GetSportsEventsParams) => {
    const halfYearAgo = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const polyLocale = params.locale ? getPolymarketLocale(params.locale) : undefined;
    const queryParams = {
      series_id: params.series_id,
      order: params.order ?? 'startTime',
      ascending: params.ascending ?? false,
      closed: false,
      start_time_min: `${halfYearAgo}T00:00:00Z`,
      limit: params.limit ?? 100,
      ...(polyLocale && { locale: polyLocale }),
    };
    const events = await request<PredictEvent[]>(`/gamma/events${buildQueryString(queryParams)}`);
    return normalizeEvents(events);
  },

  /** 获取正在直播的体育赛事 */
  getLiveSportsEvents: async (params?: { limit?: number; locale?: string }) => {
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const polyLocale = params?.locale ? getPolymarketLocale(params.locale) : undefined;
    const queryParams = {
      live: true,
      start_time_min: `${yesterday}T00:00:00Z`,
      limit: params?.limit ?? 200,
      ...(polyLocale && { locale: polyLocale }),
    };
    const events = await request<PredictEvent[]>(`/gamma/events${buildQueryString(queryParams)}`);
    return normalizeEvents(events);
  },

  // ============ Geoblock API ============

  /** 检查当前 IP 是否受地区限制 */
  checkGeoblock: async (): Promise<{ blocked: boolean; country: string; region: string }> => {
    try {
      const res = await fetchWithCapture('predict:polymarket/geoblock', 'https://polymarket.com/api/geoblock');
      return res.json();
    } catch {
      // 请求失败时默认不限制
      return { blocked: false, country: '', region: '' };
    }
  },

  // ============ Order API ============

  /** 上报订单数据
   * inviterId 语义：
   *  - source=live_* → 主播 userId
   *  - source=talk_card → 发帖人 userId
   * sourceId：source=live_* 传 roomId，source=talk_card 传 talkId
   */
  reportOrder: (data: {
    orderId: string;
    orderType: 'limit' | 'market';
    orderResponse: Record<string, unknown>;
    orderRequest?: Record<string, unknown>;
    inviterId?: number;
    source?: 'live_ai_card' | 'live_tab' | 'live_shop' | 'talk_card';
    sourceId?: string;
    showData?: Record<string, unknown>;
    feePermit?: {
      permit?: { permitted: { token: string; amount: string }; spender: string; nonce: string; deadline: number };
      signature?: string;
      feeAmount?: number;
      safeAddress?: string;
      // 签名失败/跳过时的原因（成功时不传）
      message?: string;
    };
  }) => {
    // 邀请人为当前用户(自己邀请自己)时不上报 inviterId
    const currentUserId = useUserStore.getState().userId;
    const payload = data.inviterId && currentUserId && data.inviterId === currentUserId ? { ...data, inviterId: undefined } : data;
    return apiClient.post('/app/prediction/order/report', payload);
  },

  /** 通过 conditionId 批量获取市场信息 */
  getMarketsByConditionIds: async (conditionIds: string[], locale?: string): Promise<Map<string, { title: string; icon?: string }>> => {
    const result = new Map<string, { title: string; icon?: string }>();
    if (conditionIds.length === 0) return result;

    // 去重
    const uniqueIds = [...new Set(conditionIds)];

    // 并行查询
    const polyLocale = locale ? getPolymarketLocale(locale) : undefined;
    const localeParam = polyLocale ? `&locale=${polyLocale}` : '';

    await Promise.all(
      uniqueIds.map(async conditionId => {
        try {
          const markets = normalizeMarkets(await request<PredictMarket[]>(`/gamma/markets?condition_ids=${conditionId}${localeParam}`));
          if (markets.length > 0) {
            result.set(conditionId, {
              title: markets[0].question || markets[0].groupItemTitle || 'Unknown',
              icon: markets[0].image || markets[0].icon,
            });
          }
        } catch (e) {
          console.warn(`[PredictAPI] 获取市场信息失败: ${conditionId}`, e);
        }
      })
    );

    return result;
  },

  /** 通过 conditionId 获取市场详情（含 clobTokenIds） */
  getMarketByConditionId: async (conditionId: string): Promise<PredictMarket | null> => {
    if (!conditionId) return null;
    try {
      const markets = normalizeMarkets(await request<PredictMarket[]>(`/gamma/markets?condition_ids=${conditionId}`));
      return markets?.[0] || null;
    } catch (e) {
      console.warn('[PredictAPI] getMarketByConditionId failed:', conditionId, e);
      return null;
    }
  },
};

// ============ Fee Config API ============

/** 手续费配置 */
export interface FeeConfig {
  feeRate: number; // 费率
  permitDuration: number; // permit 有效期（秒）
  feeReceiverAddress: string; // 收款钱包地址
}

/** 获取手续费配置 */
export async function getFeeConfig(): Promise<FeeConfig | null> {
  try {
    const res = await apiClient.get('/app/prediction/fee-config');
    const resAny = res as any;
    // 后端字段平铺在根级别，不在 data 中
    if (resAny.code === 200 && resAny.feeRate !== undefined) {
      return {
        feeRate: resAny.feeRate,
        permitDuration: resAny.permitDuration,
        feeReceiverAddress: resAny.feeReceiverAddress,
      };
    }
    return null;
  } catch (err) {
    console.error('[PredictAPI] getFeeConfig failed:', err);
    return null;
  }
}

export default predictApi;
